import { useEffect, useState } from 'react';
import { Alert, AlertColor, Box, Card, CardContent, CircularProgress, Grid, Paper, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { useVForm } from 'shared/forms';

import { FerramentasDeDetalhe, InputError, ConfirmacaoModal } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { t } from 'i18next';
import { DadosVariaveisService } from 'shared/services/dadosVariaveis/dadosVariaveisService';
import { ICreateDadosVariaveis, InicitalCreateDadosVariaveis } from 'shared/types';
import { useHasPermissionAcao } from 'shared/hooks';


const validationSchema = yup.object({
  TagDadosVariaveis: yup.string().required(),
  NomeDadosVariaveis: yup.string().required(),
  TamMaxDadosVariaveis: yup.number().required().moreThan(0),
  DescDadosVariaveis: yup.string().required(),
});

export const DetalheDeDadosVariaveis: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();

  const hasPermitionCreate = useHasPermissionAcao('dadosvariaveis','CRE');
  const hasPermitionDelete = useHasPermissionAcao('dadosvariaveis','DEL');
  const hasPermitionUpdate = useHasPermissionAcao('dadosvariaveis','UPD');

  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const { formRef, save, isSaveAndClose } = useVForm();

  const { register, handleSubmit, reset, formState: { errors }, } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onBlur'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [titleConfirmacao, setTitleConfirmacao] = useState('');
  const [messageConfirmacao, setMessageConfirmacao] = useState('');

  useEffect(() => {
    setAlert(null);
    if (id !== 'novo') {
      setIsLoading(true);

      DadosVariaveisService.getById(id)
        .then((result) => {
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
            navigate('/dadosvariaveis/listagem');
          } else {
            delete result.ROW_NUM;
            reset(result);
          }
          setIsLoading(false);
        });
    }
    else {
      reset(InicitalCreateDadosVariaveis);
    }

  }, [id]);

  const handleDelete = () => {
    setTitleConfirmacao(t('components.actions.apagar'));
    setMessageConfirmacao(t('messages.dadosVariaveis.exclusion') + id + '?');
    setOpenConfirmacao(true);
    console.log('handleDelete');
  };

  const confirmDelete = () => {
    setAlert(null);
    DadosVariaveisService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setAlert({
            text: result.message,
            type: 'error',
          });
          setOpenConfirmacao(false);
        } else {
          setAlert({
            text: 'Registro apagado com sucesso!',
            type: 'success',
          });
          navigate('/dadosvariaveis/listagem');
        }
      })
      .catch( reason => {
        setAlert({
          text: reason,
          type: 'error',
        });
        setOpenConfirmacao(false);
      });
  };

  const onSubmit = async (data: ICreateDadosVariaveis) => {
    setAlert(null);
    setIsSubmitting(true);
    if (id === 'novo') {
      DadosVariaveisService
        .create(data)
        .then((result) => { 
          setIsLoading(false);

          if (result instanceof Error) {  
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/dadosvariaveis/listagem');
            } else {
              navigate(`/dadosvariaveis/detalhe/${result}`);
            }
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    } else {
      DadosVariaveisService
        .updateById(id, data)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/dadosvariaveis/listagem');
            }
            setAlert({
              text: t('messages.success.alterado'), 
              type: 'success',
            });

          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    }

  };



  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Dado Variável' : hasPermitionUpdate?'Alterar Dado Variável':'Consultar Dado Variável'}
      barraDeFerramentas={!(isLoading || isSubmitting) && (
        <FerramentasDeDetalhe
          idForm='formDetalheDeDadosVariaveis'
          textoBotaoNovo={t('components.actions.novo')}

          mostrarBotaoNovo={id !== 'novo' && hasPermitionCreate}
          mostrarBotaoApagar={id !== 'novo' && hasPermitionDelete}
          mostrarBotaoSalvar={hasPermitionCreate || hasPermitionUpdate}

          aoClicarEmSalvar={save}
          aoClicarEmVoltar={() => navigate('/dadosvariaveis/listagem')}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/dadosvariaveis/detalhe/novo')}
        />)
      }
    >
      <Box ref={formRef} id='formDetalheDeDadosVariaveis' component="form" display="flex" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" width='100%' >

          <Card >
            <CardContent sx={{ padding: 0 }} >
              <div>
                {alert ? (
                  <>
                    <Grid item xs={12} paddingX={2}>
                      <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ marginTop: '1rem', marginBottom: '0' }}>
                        {alert.text}
                      </Alert>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} component={Typography} paddingX={2}>

                  </Grid>
                )}
                <Grid container  >
                  <Grid item xs={12} paddingX={2}>
                    <TextField
                      error={errors?.TagDadosVariaveis?.type ? true : false}
                      fullWidth
                      id='TagDadosVariaveis'
                      type='text'
                      label={`${t('pages.dadosVariaveis.tagDadosVariaveis')} *`}
                      disabled={isSubmitting || isLoading || id !== 'novo'}
                      {...register('TagDadosVariaveis')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.TagDadosVariaveis?.type && <InputError type={errors.TagDadosVariaveis.type} field='TagDadosVariaveis' />}
                  </Grid>
                  <Grid item xs={12} paddingX={2}>
                    <TextField
                      error={errors?.NomeDadosVariaveis?.type ? true : false}
                      fullWidth
                      id='NomeDadosVariaveis'
                      type='text'
                      label={`${t('pages.dadosVariaveis.nomeDadosVariaveis')} *`}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('NomeDadosVariaveis')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.NomeDadosVariaveis?.type && <InputError type={errors.NomeDadosVariaveis.type} field='NomeDadosVariaveis' />}
                  </Grid>
                  <Grid item xs={12} paddingX={2}>
                    <TextField
                      error={errors?.TamMaxDadosVariaveis?.type ? true : false}
                      fullWidth
                      id='TamMaxDadosVariaveis'
                      type='number'
                      label={`${t('pages.dadosVariaveis.tamMaxDadosVariaveis')} *`}
                      disabled={isSubmitting || isLoading || id !== 'novo'}
                      {...register('TamMaxDadosVariaveis')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.TamMaxDadosVariaveis?.type && <InputError type={errors.TamMaxDadosVariaveis.type} field='TamMaxDadosVariaveis' />}
                  </Grid>
                  <Grid item xs={12} paddingX={2}>
                    <TextField
                      error={errors?.DescDadosVariaveis?.type ? true : false}
                      fullWidth
                      id='DescDadosVariaveis'
                      type='text'
                      label={`${t('pages.dadosVariaveis.descDadosVariaveis')} *`}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('DescDadosVariaveis')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.DescDadosVariaveis?.type && <InputError type={errors.DescDadosVariaveis.type} field='DescDadosVariaveis' />}
                  </Grid>
                </Grid>
              </div>
            </CardContent>
          </Card>
        </Box>
        <ConfirmacaoModal
          open={openConfirmacao}
          title={titleConfirmacao}
          message={messageConfirmacao}
          onClose={() => setOpenConfirmacao(false)}
          onConfirm={() => confirmDelete()}
        />
      </Box>
      {(isLoading || isSubmitting) && (<Box sx={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress />
      </Box>)}

    </LayoutBaseDePagina >
  );
};
